import Select from "@carrier-io/air-react/Select";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import React, { forwardRef } from "react";
import Input from "@carrier-io/air-react/Input";
import InputLabel from "@carrier-io/air-react/InputLabel";
import FormControl from "@carrier-io/air-react/FormControl";
import FormHelperText from "@carrier-io/air-react/FormHelperText";
import { useTheme } from "@carrier-io/air-react/styles";

type ControllableSelectProps<T extends FieldValues> = {
  id: string;
  name: Path<T>;
  label: string;
  control: Control<T>;
  error?: string;
  formControlProps?: React.ComponentProps<typeof FormControl>;
  inputProps?: React.ComponentProps<typeof Input>;
  inputLabelProps?: React.ComponentProps<typeof InputLabel>;
} & Omit<React.ComponentProps<typeof Select>, "error">;

export const ControllableSelect = forwardRef(function ControllableSelect<
  T extends FieldValues,
>(props: ControllableSelectProps<T>, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    name,
    label,
    control,
    error,
    inputProps,
    formControlProps,
    inputLabelProps,
    ...selectProps
  } = props;
  const theme = useTheme();

  return (
    <FormControl ref={ref} variant="filled" {...formControlProps}>
      <InputLabel {...inputLabelProps}>{label}</InputLabel>
      {Controller &&
        (Controller<T>({
          name,
          control,
          render: ({
            field: { name, onChange, value, onBlur, ref },
            formState: { defaultValues },
          }) => (
            <Select
              ref={ref}
              hideBackgroundColor
              defaultValue={defaultValues?.[name] ?? ""}
              input={<Input hideBackgroundColor showBorder {...inputProps} />}
              error={!!error}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
              showBorder
              sx={{
                "& .MuiNativeSelect-select:focus": {
                  backgroundColor: "transparent",
                },
                minWidth: 200,
                width: "100%",
              }}
              value={value}
              variant="outlined"
              {...selectProps}
            >
              {selectProps.children}
            </Select>
          ),
        }) as React.ReactElement)}
      {!!error && (
        <FormHelperText
          sx={{
            marginTop: 0,
            color: theme.palette.error.main,
          }}
        >
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}) as <T extends FieldValues>(
  props: ControllableSelectProps<T> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  },
) => React.ReactElement;
